/**
 * Piece-tagging logic (FRD §7).
 *
 * Tag format: <SERVICE_CODE>-<n>/<total for that service in that room>.
 * Each service line gets its OWN sequence - a room with 20 Wash & Iron pieces
 * and 3 Dry Cleaning items prints WI-1/20 … WI-20/20 and DC-1/3 … DC-3/3, so a
 * loose tag on the sorting table is never ambiguous.
 *
 * WHERE THE TOTAL COMES FROM. This is the whole point of the file and the one
 * thing it is easy to get wrong:
 *
 *   weight services (Wash & Iron)  →  `pieceCount`, the operator's Pieces entry
 *   item services (DC / Shoe Care) →  the sum of the line quantities
 *
 * A weight line's `quantity` is its WEIGHT, not a count - the server writes
 * `quantity = weight_kg` so `amount = quantity × unit_price` holds for both
 * shapes (b2b-order.service.js). Summing it would print "WI-1/6" for a 6 kg
 * bag holding twenty shirts, and the prototype's alternative - hard-coding 20 -
 * would print twenty tags for a bag holding four. Neither is a piece count; only
 * `b2b_room_orders.piece_count` is.
 *
 * INVARIANT: the total on every tag equals the number of tags actually emitted
 * for that service. A sheet that says "of 20" and contains 6 tags is worse than
 * no sheet, because the shortfall is only discovered at the sorting table.
 */
import { serviceBySlug } from '../serviceCatalogue'
import type { EntryType } from '../types'

/** One saved line, in whichever shape the caller holds it. */
export interface TaggableLine {
  /** null for weight services (FRD §6). */
  itemName: string | null
  /** null for item services (FRD §6). */
  weightKg: number | null
  /** Pieces for an item line; KILOGRAMS for a weight line - never a count. */
  quantity: number
}

/**
 * The order shape the tag builders need, and nothing more.
 *
 * Structural on purpose: both `B2bOrderRecord` (what the server returns) and the
 * mock store's `B2bOrder` satisfy it, so the same builder serves the console and
 * the fixtures without a translation layer.
 */
export interface TaggableOrder {
  serviceSlug: string
  /** The server states it; the catalogue answers for a fixture that does not. */
  entryType?: EntryType
  /** Physical pieces to tag. Authoritative for weight services. */
  pieceCount?: number | null
  items: TaggableLine[]
}

export interface PieceTag {
  serviceSlug: string
  serviceLabel: string
  serviceCode: string
  sequence: number
  total: number
  /** "WI-1/20" - what gets printed. */
  label: string
  /** Item-based services print the garment; weight-based print the weight. */
  itemName: string | null
  weightKg: number | null
}

const wholeCount = (value: unknown): number => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return 0
  return Math.max(0, Math.trunc(parsed))
}

const entryTypeOf = (order: TaggableOrder): EntryType =>
  order.entryType ?? serviceBySlug(order.serviceSlug)?.entryType ?? 'items'

export const isWeightEntry = (order: TaggableOrder): boolean => entryTypeOf(order) === 'weight'

/** How many tags this service will print for this room. */
export const orderPieceTotal = (order: TaggableOrder): number =>
  isWeightEntry(order)
    ? wholeCount(order.pieceCount)
    : order.items.reduce((sum, item) => sum + wholeCount(item.quantity), 0)

/**
 * Why this order cannot be tagged, or null when it can.
 *
 * A weight line saved without a piece count is the case that matters: the
 * operator weighed the bag and moved on, which the save path deliberately
 * allows. Printing it anyway would put tags on garments that claim a total
 * nobody counted, so the service is blocked HERE and the reason travels with it
 * to the print dialog.
 */
export interface TagBlocker {
  serviceSlug: string
  serviceLabel: string
  reason: string
}

export const tagBlocker = (order: TaggableOrder): TagBlocker | null => {
  const label = serviceBySlug(order.serviceSlug)?.label ?? order.serviceSlug
  if (orderPieceTotal(order) > 0) return null

  return {
    serviceSlug: order.serviceSlug,
    serviceLabel: label,
    reason: isWeightEntry(order)
      ? `${label} has no piece count. Enter Pieces for this room and save before printing its tags.`
      : `${label} has no items to tag.`,
  }
}

export const buildOrderTags = (order: TaggableOrder): PieceTag[] => {
  const service = serviceBySlug(order.serviceSlug)
  const serviceCode = service?.code ?? order.serviceSlug.slice(0, 2).toUpperCase()
  const serviceLabel = service?.label ?? order.serviceSlug
  const total = orderPieceTotal(order)

  if (total <= 0) return []

  const tag = (sequence: number, itemName: string | null, weightKg: number | null): PieceTag => ({
    serviceSlug: order.serviceSlug,
    serviceLabel,
    serviceCode,
    sequence,
    total,
    label: `${serviceCode}-${sequence}/${total}`,
    itemName,
    weightKg,
  })

  if (isWeightEntry(order)) {
    // One line, and it carries the weight the whole bag was priced on. Every
    // piece in that bag is tagged with it - the operator reads the weight to
    // confirm they are holding the right bag, not to price a single shirt.
    const weightKg = order.items[0]?.weightKg ?? null
    return Array.from({ length: total }, (_, index) => tag(index + 1, null, weightKg))
  }

  const tags: PieceTag[] = []
  let sequence = 0

  order.items.forEach((item) => {
    const quantity = wholeCount(item.quantity)
    for (let index = 0; index < quantity; index += 1) {
      sequence += 1
      tags.push(tag(sequence, item.itemName, item.weightKg))
    }
  })

  return tags
}

export const buildRoomTags = (orders: TaggableOrder[]): PieceTag[] =>
  orders.flatMap((order) => buildOrderTags(order))

/** Every service in this room that cannot be tagged, with the reason. */
export const roomTagBlockers = (orders: TaggableOrder[]): TagBlocker[] =>
  orders.map((order) => tagBlocker(order)).filter((blocker): blocker is TagBlocker => blocker !== null)

/** "WI 1–20, DC 1–3" - printed in the room slip footer. */
export const tagRangeSummary = (orders: TaggableOrder[]): string =>
  orders
    .map((order) => {
      const total = orderPieceTotal(order)
      if (total <= 0) return null
      const code = serviceBySlug(order.serviceSlug)?.code ?? order.serviceSlug.slice(0, 2).toUpperCase()
      return total === 1 ? `${code} 1` : `${code} 1–${total}`
    })
    .filter(Boolean)
    .join(', ')
